import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Grid, MenuItem, Alert, CircularProgress,
  LinearProgress, Avatar, Switch, FormControlLabel, InputAdornment, Divider, Chip,
} from '@mui/material';
import { Business, Save, PhotoCamera, Delete, Lock, Instagram, Facebook, Language, SmartToy, CheckCircle } from '@mui/icons-material';
import { api } from '../config/api';
import { useAuth } from '../context/AuthContext';
import InfoTip from '../components/InfoTip';
import { INDUSTRIAS_PERFIL, TIPOS_PYME } from '../content/plantillasAgente';

/** Reduce la imagen a máx. 256 px (lado mayor) y la devuelve como data URL PNG/WEBP. */
function reducirImagen(file: File, max = 256): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const escala = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * escala));
      const h = Math.max(1, Math.round(img.height * escala));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('No se pudo procesar la imagen')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      const webp = canvas.toDataURL('image/webp', 0.9);
      resolve(webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('El archivo no es una imagen válida')); };
    img.src = url;
  });
}

const CAMPOS_AVANCE = ['nombre_fantasia', 'industria', 'tipo_pyme', 'descripcion', 'telefono_contacto', 'email_contacto', 'direccion', 'horario_texto', 'medios_pago', 'logo_data_url'];

const Perfil: React.FC = () => {
  const { refresh } = useAuth();
  const [usuario, setUsuario] = useState<any>(null);
  const [empresa, setEmpresa] = useState<any>({ usar_en_agente: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ sev: any; txt: string } | null>(null);
  const [campoError, setCampoError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [pass, setPass] = useState({ actual: '', nueva: '', repetir: '' });
  const [passMsg, setPassMsg] = useState<{ sev: any; txt: string } | null>(null);

  useEffect(() => {
    api.get('/perfil').then((r) => { setUsuario(r.data.usuario); setEmpresa(r.data.empresa || { usar_en_agente: true }); }).finally(() => setLoading(false));
  }, []);

  const setE = (k: string) => (e: any) => setEmpresa({ ...empresa, [k]: e.target.value });
  const setU = (k: string) => (e: any) => setUsuario({ ...usuario, [k]: e.target.value });
  const avance = Math.round((CAMPOS_AVANCE.filter((c) => empresa?.[c] && String(empresa[c]).trim()).length / CAMPOS_AVANCE.length) * 100);
  const notificar = (logo?: string | null) => window.dispatchEvent(new CustomEvent('respondo:perfil-actualizado', { detail: { logo } }));

  const guardar = async () => {
    setSaving(true); setMsg(null); setCampoError('');
    try {
      const { logo_data_url, ...datos } = empresa;
      const { data } = await api.put('/perfil', { usuario: { nombre: usuario.nombre, telefono: usuario.telefono }, empresa: datos });
      setUsuario(data.usuario); setEmpresa(data.empresa || datos);
      setMsg({ sev: 'success', txt: 'Perfil guardado.' + (datos.usar_en_agente !== false ? ' Tu agente ya usa estos datos.' : '') });
      refresh();
    } catch (e: any) {
      setMsg({ sev: 'error', txt: e.response?.data?.error || 'No se pudo guardar' });
      setCampoError(e.response?.data?.field || '');
    } finally { setSaving(false); }
  };

  const subirLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setMsg({ sev: 'error', txt: 'La imagen supera 8 MB.' }); return; }
    try {
      const dataUrl = await reducirImagen(file);
      const { data } = await api.put('/perfil/logo', { logo: dataUrl });
      setEmpresa({ ...empresa, logo_data_url: data.logo });
      notificar(data.logo);
      setMsg({ sev: 'success', txt: 'Logo actualizado.' });
    } catch (err: any) { setMsg({ sev: 'error', txt: err.response?.data?.error || err.message || 'No se pudo subir el logo' }); }
  };

  const quitarLogo = async () => {
    await api.delete('/perfil/logo');
    setEmpresa({ ...empresa, logo_data_url: null });
    notificar(null);
  };

  const cambiarPassword = async () => {
    setPassMsg(null);
    if (pass.nueva !== pass.repetir) { setPassMsg({ sev: 'error', txt: 'Las contraseñas nuevas no coinciden.' }); return; }
    try {
      await api.post('/perfil/password', { actual: pass.actual, nueva: pass.nueva });
      setPass({ actual: '', nueva: '', repetir: '' });
      setPassMsg({ sev: 'success', txt: 'Contraseña actualizada.' });
    } catch (e: any) { setPassMsg({ sev: 'error', txt: e.response?.data?.error || 'No se pudo cambiar la contraseña' }); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  const err = (c: string) => campoError === c;

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Business color="primary" />
        <Typography variant="h4">Mi perfil</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Los datos de tu negocio. Tu agente los usa para responder dirección, horarios, medios de pago y más.
      </Typography>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.txt}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar src={empresa.logo_data_url || undefined} variant="rounded"
                sx={{ width: 120, height: 120, mx: 'auto', mb: 1.5, bgcolor: '#E8F8EE', color: 'primary.dark', fontSize: 44, border: '1px solid #E2E8F0' }}>
                {(empresa.nombre_fantasia || usuario?.nombre || '?')[0]?.toUpperCase()}
              </Avatar>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={subirLogo} />
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button size="small" variant="outlined" startIcon={<PhotoCamera />} onClick={() => fileRef.current?.click()}>
                  {empresa.logo_data_url ? 'Cambiar logo' : 'Subir logo'}
                </Button>
                {empresa.logo_data_url && <Button size="small" color="error" startIcon={<Delete />} onClick={quitarLogo}>Quitar</Button>}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>PNG, JPG o WEBP. Idealmente cuadrado. Lo ajustamos solo.</Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2">Perfil {avance}% completo</Typography>
              <LinearProgress variant="determinate" value={avance} color={avance === 100 ? 'success' : 'primary'} sx={{ height: 8, borderRadius: 4, my: 1 }} />
              <Typography variant="caption" color="text.secondary">Mientras más completo, mejor responde tu agente.</Typography>
              <Divider sx={{ my: 1.5 }} />
              <FormControlLabel
                control={<Switch checked={empresa.usar_en_agente !== false} onChange={(e) => setEmpresa({ ...empresa, usar_en_agente: e.target.checked })} />}
                label={<span><SmartToy sx={{ fontSize: 18, verticalAlign: 'text-bottom' }} /> Usar estos datos en mi agente</span>}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Se agregan como “datos oficiales de la empresa”. No incluye RUT ni razón social.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}><Lock sx={{ verticalAlign: 'middle', mr: 0.5 }} />Contraseña</Typography>
              {passMsg && <Alert severity={passMsg.sev} sx={{ mb: 1.5 }} onClose={() => setPassMsg(null)}>{passMsg.txt}</Alert>}
              <Stack spacing={1.5}>
                <TextField size="small" type="password" label="Contraseña actual" value={pass.actual} onChange={(e) => setPass({ ...pass, actual: e.target.value })} autoComplete="current-password" />
                <TextField size="small" type="password" label="Nueva contraseña" value={pass.nueva} onChange={(e) => setPass({ ...pass, nueva: e.target.value })} helperText="Mínimo 6 caracteres" autoComplete="new-password" />
                <TextField size="small" type="password" label="Repetir nueva" value={pass.repetir} onChange={(e) => setPass({ ...pass, repetir: e.target.value })} autoComplete="new-password" />
                <Button variant="outlined" onClick={cambiarPassword} disabled={!pass.actual || !pass.nueva}>Cambiar contraseña</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Tus datos</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre" value={usuario?.nombre || ''} onChange={setU('nombre')} error={err('nombre')} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono personal" value={usuario?.telefono || ''} onChange={setU('telefono')} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Email de acceso" value={usuario?.email || ''} disabled helperText="Para cambiarlo, escribe a soporte" /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Tu empresa</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre del negocio" value={empresa.nombre_fantasia || ''} onChange={setE('nombre_fantasia')} placeholder="Ej: Pastelería Dulce Hogar" /></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Rubro" value={empresa.industria || ''} onChange={setE('industria')} helperText="Lo usamos para recomendarte plantillas">
                    {INDUSTRIAS_PERFIL.map((i) => <MenuItem key={i.id} value={i.id}>{i.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Cómo vendes" value={empresa.tipo_pyme || ''} onChange={setE('tipo_pyme')}>
                    {TIPOS_PYME.map((t) => <MenuItem key={t.id} value={t.id}>{t.emoji} {t.nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="RUT" value={empresa.rut || ''} onChange={setE('rut')} placeholder="76.123.456-7" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Razón social" value={empresa.razon_social || ''} onChange={setE('razon_social')} /></Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} label="¿A qué se dedica tu negocio?" value={empresa.descripcion || ''} onChange={setE('descripcion')}
                    placeholder="Ej: Hacemos tortas y pasteles artesanales a pedido, con despacho en Santiago oriente."
                    InputProps={{ endAdornment: <InputAdornment position="end"><InfoTip texto="Una o dos frases. El agente la usa cuando le preguntan “¿qué hacen?”." /></InputAdornment> }} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Atención al cliente <Chip size="small" label="El agente lo usa" color="success" sx={{ ml: 1 }} /></Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono / WhatsApp de contacto" value={empresa.telefono_contacto || ''} onChange={setE('telefono_contacto')} placeholder="+56 9 1234 5678" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Email de contacto" value={empresa.email_contacto || ''} onChange={setE('email_contacto')} error={err('email_contacto')} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Dirección" value={empresa.direccion || ''} onChange={setE('direccion')} placeholder="Av. Italia 1234, local 5" /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth label="Comuna" value={empresa.comuna || ''} onChange={setE('comuna')} /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth label="Ciudad" value={empresa.ciudad || ''} onChange={setE('ciudad')} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Horario de atención" value={empresa.horario_texto || ''} onChange={setE('horario_texto')} placeholder="Lunes a viernes 10:00-19:00, sábado 10:00-14:00" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Medios de pago" value={empresa.medios_pago || ''} onChange={setE('medios_pago')} placeholder="Transferencia, débito, crédito, efectivo" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Zonas de despacho / cobertura" value={empresa.zonas_despacho || ''} onChange={setE('zonas_despacho')} placeholder="Providencia, Ñuñoa y Las Condes" /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Web y redes</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Sitio web" value={empresa.sitio_web || ''} onChange={setE('sitio_web')} InputProps={{ startAdornment: <InputAdornment position="start"><Language fontSize="small" /></InputAdornment> }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Instagram" value={empresa.instagram || ''} onChange={setE('instagram')} placeholder="@tunegocio" InputProps={{ startAdornment: <InputAdornment position="start"><Instagram fontSize="small" /></InputAdornment> }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Facebook" value={empresa.facebook || ''} onChange={setE('facebook')} InputProps={{ startAdornment: <InputAdornment position="start"><Facebook fontSize="small" /></InputAdornment> }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="TikTok" value={empresa.tiktok || ''} onChange={setE('tiktok')} placeholder="@tunegocio" /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ position: 'sticky', bottom: 16, mt: 3, textAlign: 'right' }}>
        <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : avance === 100 ? <CheckCircle /> : <Save />} onClick={guardar} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar perfil'}
        </Button>
      </Box>
    </Box>
  );
};

export default Perfil;
